import { Buildings, ClipboardText, Cpu, GearSix, House, ShieldCheck, type Icon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { operatorRoleLabel } from '@/lib/format';
import { t } from '@/lib/i18n';
import { ACCOUNT_LINKS, DESTINATIONS, paths, visible, type AccountLink, type Destination } from '@/lib/nav';
import { useOperator } from '@/lib/operator';
import { AccountMenu, AppShell, AuthLayout, ThemeToggle, Toolbar, type NavLeaf } from '@/ui';

/**
 * The two frames every route sits in: the signed-in shell, and the sign-in
 * split screen.
 *
 * What is shown, and gated by what, comes from `lib/nav.ts`; this file only
 * draws it. Toolbar, never a sidebar.
 */

/** One glyph per destination. A `Record`, so a destination added to `lib/nav.ts` without one does not compile. */
const DESTINATION_ICON: Readonly<Record<Destination['key'], Icon>> = {
  dashboard: House,
  tenants: Buildings,
  permissions: ShieldCheck,
  models: Cpu,
  audit: ClipboardText,
};

const ACCOUNT_LINK_ICON: Readonly<Record<AccountLink['key'], Icon>> = {
  defaults: GearSix,
};

/**
 * The platform's mark: تحوّل, the operator company — not any one المنشأة's.
 * أيد is a customer here like any other; a المنشأة's own logo appears only
 * inside its workspace (the context bar). Both places this is drawn — the
 * toolbar and the sign-in brand panel — are ink in both themes, so the white
 * wordmark; `tahwool-navy.svg` is for a light surface, should one carry it.
 */
const BRAND_ON_INK = '/brand/tahwool-white.svg';

function Brand({ variant }: { variant: 'toolbar' | 'auth' }) {
  // On the toolbar the logo is the way home; on the sign-in panel there is no home yet.
  if (variant === 'toolbar') {
    return (
      <Link to={paths.dashboard}>
        <img src={BRAND_ON_INK} alt={t('nav.brand')} />
      </Link>
    );
  }

  return (
    <span className="brand-lockup">
      <img src={BRAND_ON_INK} alt={t('nav.brand')} />
      <span className="brand-caption">{t('nav.appName')}</span>
    </span>
  );
}

/**
 * The signed-in frame. The toolbar holds the platform's five pages — one flat
 * row, no dropdown groups. A المنشأة's own pages are not here: they are the
 * second bar its workspace draws under this one (`tenant-bar.tsx`).
 */
export function Layout() {
  const { operator, has, signOut } = useOperator();
  const { pathname } = useLocation();

  if (operator === null) return null;

  const items: NavLeaf[] = visible(DESTINATIONS, has).map((destination) => ({
    to: destination.to,
    label: t(destination.label),
    icon: DESTINATION_ICON[destination.key],
    end: destination.end,
  }));

  return (
    <AppShell
      toolbar={
        <Toolbar
          brand={<Brand variant="toolbar" />}
          product={t('nav.appName')}
          items={items}
          actions={<ThemeToggle />}
          account={
            <AccountMenu
              name={operator.displayName}
              email={operator.email}
              role={operatorRoleLabel(operator.role)}
              onSignOut={() => void signOut()}
              links={visible(ACCOUNT_LINKS, has).map((link) => ({
                label: t(link.label),
                to: link.to,
                icon: ACCOUNT_LINK_ICON[link.key],
                current: pathname === link.to,
              }))}
            />
          }
        />
      }
    >
      <Outlet />
    </AppShell>
  );
}

/**
 * The sign-in frame: the brand panel and its promise (brief §2.1) are drawn
 * here, so the sign-in page author writes only the form that goes beside them.
 */
export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <AuthLayout
      brand={<Brand variant="auth" />}
      headline={t('frame.authHeadline')}
      lead={t('frame.authLead')}
      points={[
        { icon: <Buildings size={20} aria-hidden="true" />, text: t('frame.authPointTenants') },
        { icon: <ShieldCheck size={20} aria-hidden="true" />, text: t('frame.authPointBoundary') },
        { icon: <ClipboardText size={20} aria-hidden="true" />, text: t('frame.authPointAudit') },
      ]}
      footnote={t('frame.authFootnote')}
      corner={<ThemeToggle onToolbar={false} />}
    >
      {children}
    </AuthLayout>
  );
}
